/**
 * Shared ordering for org assessment question sets and questions.
 * Used by org trends, squad scorecard, and other org visualizations.
 *
 * Sort priority:
 * 1. Items with ordinal (ascending)
 * 2. Items without ordinal (by inserted_at, then label, then key)
 */

export function normalizeOrdinal(value) {
  if (value == null || value === '') {
    return null;
  }

  const normalized = Number(value);

  return Number.isFinite(normalized) ? normalized : null;
}

function ordinalPresenceRank(ordinal) {
  return normalizeOrdinal(ordinal) == null ? 1 : 0;
}

export function compareInsertedAt(a, b) {
  const timeA = a ? new Date(a).getTime() : Number.NaN;
  const timeB = b ? new Date(b).getTime() : Number.NaN;
  const validA = Number.isFinite(timeA);
  const validB = Number.isFinite(timeB);

  if (!validA && !validB) {
    return 0;
  }

  if (!validA) {
    return 1;
  }

  if (!validB) {
    return -1;
  }

  return timeA - timeB;
}

export function compareByOrdinalThenLabels(a, b, nameKey = 'name') {
  const presenceRank =
    ordinalPresenceRank(a.ordinal) - ordinalPresenceRank(b.ordinal);

  if (presenceRank !== 0) {
    return presenceRank;
  }

  const ordA = normalizeOrdinal(a.ordinal);
  const ordB = normalizeOrdinal(b.ordinal);

  if (ordA != null && ordB != null && ordA !== ordB) {
    return ordA - ordB;
  }

  if (ordA == null && ordB == null) {
    const insertedCompare = compareInsertedAt(a.inserted_at, b.inserted_at);

    if (insertedCompare !== 0) {
      return insertedCompare;
    }
  }

  const nameCompare = String(a[nameKey] ?? '').localeCompare(
    String(b[nameKey] ?? ''),
    undefined,
    { sensitivity: 'base' }
  );

  if (nameCompare !== 0) {
    return nameCompare;
  }

  return String(a.key ?? '').localeCompare(String(b.key ?? ''), undefined, {
    sensitivity: 'base',
  });
}

export function compareQuestionSetOrder(a, b) {
  return compareByOrdinalThenLabels(a, b, 'name');
}

export function compareQuestionOrder(a, b) {
  return compareByOrdinalThenLabels(a, b, 'text');
}

export function sortQuestionSets(questionSets = []) {
  return [...questionSets].sort(compareQuestionSetOrder);
}

export function sortQuestions(questions = []) {
  return [...questions].sort(compareQuestionOrder);
}

export function extractTemplateQuestionSets(template) {
  const assessment = template?.assessment ?? template;

  if (!assessment) {
    return [];
  }

  if (
    Array.isArray(assessment.question_sets) &&
    assessment.question_sets.length
  ) {
    return assessment.question_sets;
  }

  const pages = assessment.pages;

  if (!pages || typeof pages !== 'object') {
    return [];
  }

  return Object.values(pages)
    .filter((page) => page.pageType === 'questionSet' && page.key)
    .sort((a, b) =>
      compareByOrdinalThenLabels(
        { ordinal: a.ordinal, name: a.name ?? a.key, key: a.key },
        { ordinal: b.ordinal, name: b.name ?? b.key, key: b.key }
      )
    )
    .map((page, pageIndex) => ({
      key: page.key,
      name: page.name ?? page.key,
      ordinal: normalizeOrdinal(page.ordinal) ?? pageIndex,
      questions: Object.values(page.elements ?? {})
        .filter((el) => el.elementType === 'question' && el.key)
        .sort((a, b) =>
          compareByOrdinalThenLabels(
            { ordinal: a.ordinal, text: a.text ?? a.name, key: a.key },
            { ordinal: b.ordinal, text: b.text ?? b.name, key: b.key },
            'text'
          )
        )
        .map((el, elIndex) => ({
          key: el.key,
          text: el.text ?? el.name ?? el.key,
          ordinal: normalizeOrdinal(el.ordinal) ?? elIndex,
        })),
    }));
}

export function buildTemplateOrdinalLookups(template) {
  const templateSets = extractTemplateQuestionSets(template);
  const questionSetOrdinals = new Map();
  const questionOrdinals = new Map();

  templateSets.forEach((qs, qsIndex) => {
    if (!qs?.key) {
      return;
    }

    questionSetOrdinals.set(qs.key, normalizeOrdinal(qs.ordinal) ?? qsIndex);

    (qs.questions ?? []).forEach((q, qIndex) => {
      if (!q?.key) {
        return;
      }

      questionOrdinals.set(
        `${qs.key}|${q.key}`,
        normalizeOrdinal(q.ordinal) ?? qIndex
      );
    });
  });

  return { questionSetOrdinals, questionOrdinals };
}

export function enrichQuestionSetsWithTemplateOrdinals(questionSets, template) {
  if (!template) {
    return questionSets;
  }

  const { questionSetOrdinals, questionOrdinals } =
    buildTemplateOrdinalLookups(template);

  return questionSets.map((qs) => ({
    ...qs,
    ordinal:
      normalizeOrdinal(qs.ordinal) ?? questionSetOrdinals.get(qs.key) ?? null,
    questions: (qs.questions ?? []).map((q) => ({
      ...q,
      ordinal:
        normalizeOrdinal(q.ordinal) ??
        questionOrdinals.get(`${qs.key}|${q.key}`) ??
        null,
    })),
  }));
}

/**
 * Order question sets and their nested questions using shared org rules.
 */
export function orderAssessmentQuestionSets(
  questionSets = [],
  assessmentTemplate = null
) {
  const enriched = enrichQuestionSetsWithTemplateOrdinals(
    questionSets,
    assessmentTemplate
  );

  return sortQuestionSets(enriched).map((qs) => ({
    ...qs,
    questions: sortQuestions(qs.questions ?? []),
  }));
}

/**
 * Assign array-index ordinals when missing (e.g. rubric documents).
 */
export function applyArrayIndexOrdinals(questionSets = []) {
  return questionSets.map((qs, qsIndex) => ({
    ...qs,
    ordinal: normalizeOrdinal(qs.ordinal) ?? qsIndex,
    questions: (qs.questions ?? []).map((q, qIndex) => ({
      ...q,
      ordinal: normalizeOrdinal(q.ordinal) ?? qIndex,
    })),
  }));
}
